import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations";

const LanguageContext = createContext(null);

const readUrlLang = () => {
  if (typeof window === "undefined") return null;
  try {
    const sp = new URLSearchParams(window.location.search);
    const q = (sp.get("lang") || "").toLowerCase();
    return q === "fr" || q === "en" ? q : null;
  } catch {
    return null;
  }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return readUrlLang() || localStorage.getItem("dataleo_lang") || "en";
  });

  // Re-read `?lang=` on history navigations so crawler-discovered alternates
  // (e.g. `/topic/planning?lang=fr`) render the correct language even if a
  // visitor previously stored a different preference in localStorage.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      const fromUrl = readUrlLang();
      if (fromUrl && fromUrl !== lang) setLang(fromUrl);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("dataleo_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const [langRoutes, setLangRoutes] = useState(null);

  const value = useMemo(
    () => ({ lang, setLang, t: translations[lang], langRoutes, setLangRoutes }),
    [lang, langRoutes],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
};
